import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarClock, FileText, Info, Landmark, Link2, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection, ReadonlyValue } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useCollections, useInvoices, useSaveCollection, useSaveInvoice, useSaveReceipt } from '../../api'
import type { Customer } from '../../types'
import { toastError, useCrm } from '../../ui'
import { Dropzone } from '../Dropzone'

const today = () => new Date().toISOString().slice(0, 10)
const amount = (msg: string) => z.string().regex(/^\d+(\.\d{1,2})?$/, msg).refine((v) => Number(v) > 0, msg)

function Footer({ pending, onCancel, label, icon }: { pending: boolean; onCancel: () => void; label?: string; icon?: ReactNode }) {
  const { t } = useTranslation()
  return (
    <>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={pending}>
        {pending ? <Loader2 className="animate-spin" /> : icon}
        {label ?? t('common.save')}
      </Button>
    </>
  )
}

function useCollectionOptions(customerId: string) {
  const collections = useCollections(customerId)
  const list = collections.data ?? []
  return { list, options: list.map((c) => ({ value: c.id, label: `${c.code} · ${fmtMoney(c.amount)}`, description: `${fmtDate(c.dueDate)} · ${c.description}` })) }
}

export function CollectionForm({ customer, onDone }: { customer: Customer; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCollection()
  const schema = z.object({ dueDate: z.string().min(1, t('crm.form.dateRequired')), amount: amount(t('crm.form.amountInvalid')), description: z.string().trim().min(2, t('crm.collection.descRequired')).max(120) })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { dueDate: today(), amount: '', description: '' } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ input: { ...v, customerId: customer.id } })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Modal open onOpenChange={(v) => !v && onDone()} dismissible={!save.isPending} icon={<CalendarClock />} title={t('crm.collection.createTitle')} description={t('crm.collection.hint', { name: customer.name })} onSubmit={(e) => void onSubmit(e)} footer={<Footer pending={save.isPending} onCancel={onDone} />}>
      <FormSection>
        <Field id="co-amount" label={t('crm.fields.amount')} error={errors.amount?.message}>
          <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="co-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.amount} />} />
        </Field>
        <Field id="co-due" label={t('crm.collection.dueDate')} error={errors.dueDate?.message}>
          <Controller control={form.control} name="dueDate" render={({ field }) => <DateField id="co-due" value={field.value} onChange={field.onChange} clearable={false} />} />
        </Field>
        <Field id="co-desc" label={t('crm.fields.description')} error={errors.description?.message} full>
          <Input id="co-desc" placeholder={t('crm.collection.descPlaceholder')} {...form.register('description')} />
        </Field>
      </FormSection>
    </Modal>
  )
}

/** Mevcut bir faturayı sisteme kaydeder; fatura kesme işlemi değildir. */
export function InvoiceForm({ customer, onDone }: { customer: Customer; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveInvoice()
  const { projectName } = useCrm()
  const { options } = useCollectionOptions(customer.id)
  const schema = z.object({
    number: z.string().trim().min(3, t('crm.invoice.numberRequired')).max(40),
    date: z.string().min(1, t('crm.form.dateRequired')),
    amount: amount(t('crm.form.amountInvalid')),
    collectionId: z.string(),
    file: z.object({ name: z.string(), size: z.number(), file: z.instanceof(File).optional() }).nullable().refine((f) => !!f, t('crm.invoice.fileRequired')),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { number: '', date: today(), amount: '', collectionId: '', file: null as { name: string; size: number; file?: File } | null } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ customerId: customer.id, number: v.number, date: v.date, amount: v.amount, collectionId: v.collectionId || null, fileName: v.file?.name ?? '', file: v.file?.file })
      toast.success(t('crm.invoice.saved'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'INVOICE_NUMBER_TAKEN') form.setError('number', { message: t('errors.INVOICE_NUMBER_TAKEN') })
      else toastError(e)
    }
  })
  return (
    <Modal open size="lg" onOpenChange={(v) => !v && onDone()} dismissible={!save.isPending} icon={<FileText />} title={t('crm.invoice.title')} description={t('crm.invoice.hint')} onSubmit={(e) => void onSubmit(e)} footer={<Footer pending={save.isPending} onCancel={onDone} />}>
      <div className="space-y-4">
        <FormSection>
          <Field id="iv-no" label={t('crm.invoice.number')} error={errors.number?.message}>
            <Input id="iv-no" placeholder="MBR-2026-00124" {...form.register('number')} />
          </Field>
          <Field id="iv-date" label={t('crm.invoice.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="iv-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="iv-cust" label={t('crm.fields.customer')}>
            <ReadonlyValue id="iv-cust">{customer.name}</ReadonlyValue>
          </Field>
          <Field id="iv-proj" label={t('crm.fields.project')}>
            <ReadonlyValue id="iv-proj">{projectName(customer.projectId)}</ReadonlyValue>
          </Field>
          <Field id="iv-col" label={t('crm.invoice.collection')} full>
            <FormSelect id="iv-col" control={form.control} name="collectionId" options={[{ value: '', label: t('crm.optional') }, ...options]} />
          </Field>
          <Field id="iv-amount" label={t('crm.fields.amount')} error={errors.amount?.message}>
            <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="iv-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} aria-invalid={!!errors.amount} />} />
          </Field>
          <Field id="iv-cur" label={t('crm.invoice.currency')}>
            <ReadonlyValue id="iv-cur">TRY</ReadonlyValue>
          </Field>
          <Field id="iv-file" label={t('crm.invoice.file')} error={errors.file?.message} full>
            <Controller control={form.control} name="file" render={({ field }) => <Dropzone id="iv-file" value={field.value} onChange={field.onChange} accept="application/pdf" hint={t('crm.dropzone.pdfHint')} invalid={!!errors.file} />} />
          </Field>
        </FormSection>
        <p className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/[0.06] p-3 text-xs">
          <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
          {t('crm.invoice.notIssue')}
        </p>
      </div>
    </Modal>
  )
}

/** Dekontu yükler ve ilgili tahsilatla eşleştirir; eşleştirme özeti kaydetmeden önce gösterilir. */
export function ReceiptForm({ customer, collectionId, onDone }: { customer: Customer; collectionId?: string; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveReceipt()
  const invoices = useInvoices(customer.id)
  const { projectName } = useCrm()
  const { list, options } = useCollectionOptions(customer.id)
  const open = options.filter((o) => list.find((c) => c.id === o.value)?.status !== 'paid')
  const schema = z.object({
    file: z.object({ name: z.string(), size: z.number(), file: z.instanceof(File).optional() }).nullable().refine((f) => !!f, t('crm.receipt.fileRequired')),
    date: z.string().min(1, t('crm.form.dateRequired')),
    amount: amount(t('crm.form.amountInvalid')),
    reference: z.string().trim().max(60),
    collectionId: z.string().min(1, t('crm.receipt.collectionRequired')),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { file: null as { name: string; size: number; file?: File } | null, date: today(), amount: '', reference: '', collectionId: collectionId ?? '' } })
  const { errors } = form.formState
  const [colId, amt] = useWatch({ control: form.control, name: ['collectionId', 'amount'] })
  const col = list.find((c) => c.id === colId)
  const inv = (invoices.data ?? []).find((i) => i.collectionId === colId)

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ customerId: customer.id, collectionId: v.collectionId, date: v.date, amount: v.amount, reference: v.reference, fileName: v.file?.name ?? '', file: v.file?.file })
      toast.success(t('crm.receipt.saved'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'PAYMENT_EXCEEDS_REMAINING') form.setError('amount', { message: t('errors.PAYMENT_EXCEEDS_REMAINING') })
      else toastError(e)
    }
  })
  const summary: [string, ReactNode][] = col
    ? [
        [t('crm.fields.customer'), customer.name],
        [t('crm.fields.project'), projectName(customer.projectId)],
        [t('crm.receipt.collection'), col.code],
        [t('crm.receipt.invoice'), inv?.number ?? '—'],
        [t('crm.receipt.remaining'), fmtMoney(col.remaining)],
        [t('crm.receipt.matchAmount'), <span key="m" className="font-semibold text-success">{amt ? fmtMoney(amt) : '—'}</span>],
      ]
    : []

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Landmark />}
      title={t('crm.receipt.title')}
      description={t('crm.receipt.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={<Footer pending={save.isPending} onCancel={onDone} label={t('crm.receipt.match')} icon={<Link2 />} />}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[16rem_minmax(0,1fr)]">
        <Field id="rc-file" label={t('crm.receipt.file')} error={errors.file?.message}>
          <Controller control={form.control} name="file" render={({ field }) => <Dropzone id="rc-file" value={field.value} onChange={field.onChange} accept="application/pdf,image/png,image/jpeg" hint={t('crm.dropzone.imgHint')} invalid={!!errors.file} />} />
        </Field>
        <div className="space-y-4">
          <FormSection>
            <Field id="rc-date" label={t('crm.receipt.date')} error={errors.date?.message}>
              <Controller control={form.control} name="date" render={({ field }) => <DateField id="rc-date" value={field.value} onChange={field.onChange} clearable={false} />} />
            </Field>
            <Field id="rc-amount" label={t('crm.fields.amount')} error={errors.amount?.message}>
              <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="rc-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.amount} />} />
            </Field>
            <Field id="rc-ref" label={t('crm.receipt.reference')} hint={t('crm.receipt.referenceHint')} full>
              <Input id="rc-ref" {...form.register('reference')} />
            </Field>
            <Field id="rc-col" label={t('crm.receipt.collection')} error={errors.collectionId?.message} full>
              <FormSelect
                id="rc-col"
                control={form.control}
                name="collectionId"
                placeholder={t('common.select')}
                options={open}
                onValueChange={(id) => {
                  const c = list.find((x) => x.id === id)
                  if (c && !form.getValues('amount')) form.setValue('amount', c.remaining)
                }}
              />
            </Field>
          </FormSection>
          {col ? (
            <section className="rounded-lg border border-success/30 bg-success/[0.05] p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Link2 className="size-4 text-success" aria-hidden />
                {t('crm.receipt.summary')}
              </h3>
              <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
                {summary.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="min-w-0 truncate">{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
